// FormLiveView — the interactive, submittable Form render shown on the LIVE wheel
// (ViewWheel, craft editor enabled === false) when FORM_BLOCK_SUBMIT_ENABLED is
// on. Real typed inputs per authored field, required + email validation with
// inline errors, a hidden honeypot, submitting/success/error states, and a POST
// through the react-query submit seam. The builder keeps the static preview.
//
// Deck: concepts/builder-blocks/05-form-block (title, labelled typed fields with a
// required *, Send button, "Responses land in your Forms inbox…" helper). Tokens
// come from the shared theme/tokens `field` chrome so the live form matches the
// authoring preview's FieldPreview exactly.

import React, { useRef, useState } from "react";
import styled from "styled-components";
import { useMutation } from "react-query";
import { useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { field, fontFamily, brand, motion, focusRing, spacing } from "../../../../theme/tokens";
import { autoTextColor } from "../../../../utils/contrast";
import { useSegmentContext } from "../../../../pages/ViewWheel/SegmentContext";
import { formFieldInk } from "./formFieldInk";
import CalendarIcon from "./CalendarIcon";
import CustomPhoneInput from "../../../PhoneInput";
import { deriveCountryCode } from "../../../../pages/Auth/Signup/deriveCountry";
import { DEFAULT_PLACEHOLDER_BY_TYPE } from "../../BlockEditorPane/editors/FormEditor";
import {
  validateForm,
  isHoneypotFilled,
  buildResponsePayload,
  submitFormResponse,
  HONEYPOT_FIELD,
  LONGTEXT_MAX,
} from "./formSubmit";

// ISP-9999 (item 5): a stored date field value is a plain "YYYY-MM-DD" string
// (unchanged wire format — formSubmit.js/buildResponsePayload treat every
// non-checkbox field as a trimmed string), converted to/from a JS Date only at
// the react-datepicker boundary.
const toISODate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};
const fromISODate = (s) => {
  const m = typeof s === "string" && /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

const Wrap = styled.div`
  padding: 4px;
  font-family: ${fontFamily};
`;
// ISP-9999 (item 7): the actual flex column is the <form> itself (Wrap only
// ever wraps ONE child, the form, so a `gap`/`& > * + *` rule declared on Wrap
// never reached Title/Fields/Submit/Helper — they are the form's children,
// not Wrap's). Making <form> the flex container is also what makes the Submit
// button's `align-self: flex-end` below actually take effect — `align-self`
// only does anything on a flex/grid item, and a bare `<form>` has no `display:
// flex` of its own. Per-child margin-top (not a uniform `gap`) so the Send
// button can carry its OWN margin-top (the standard element-to-button
// spacing token) without double-counting a competing `gap` value.
const FormEl = styled.form`
  display: flex;
  flex-direction: column;
  & > * + * {
    margin-top: 14px;
  }
`;
const Title = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${field.ink};
`;
const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
// ISP-9999 (item 1): label/required-marker/helper ink is now resolved per
// render against the segment's background (formFieldInk) and passed in as
// `$ink`, instead of the fixed `field.ink`/`field.errorText`/`field.muted`
// tokens — same auto-contrast law as the poll question / blocks rule.
const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.$ink};
  margin-bottom: 4px;
`;
const Req = styled.span`
  color: ${(p) => p.$ink};
  margin-left: 3px;
`;
// ISP-9999: field border/focus-ring/checkbox accent follows the block's
// resolved accent color ($accent, segment color by default) instead of the
// fixed brand token, matching the submit button. Falls back to the app's
// brand accent if a caller renders these controls with no $accent (legacy
// usage / tests).
const withAlpha = (hex, alphaHex) => (/^#[0-9a-fA-F]{6}$/.test(hex || "") ? `${hex}${alphaHex}` : field.focusShadow);
const controlCss = (props) => {
  const accent = props.$accent || field.accent;
  return `
  width: 100%;
  box-sizing: border-box;
  min-height: 44px;
  border: 1px solid ${field.border};
  border-radius: 8px;
  padding: 10px 12px;
  font-family: ${fontFamily};
  font-size: 14px;
  color: ${field.ink};
  background: ${field.bg};
  transition: ${motion.transition("border-color, box-shadow", motion.duration.fast)};
  &::placeholder { color: ${field.placeholder}; }
  &:hover { border-color: ${field.borderHover}; }
  &:focus-visible {
    outline: none;
    border-color: ${accent};
    box-shadow: 0 0 0 3px ${withAlpha(accent, "33")};
  }
  &[aria-invalid="true"] { border-color: ${field.borderInvalid}; }
  /* ISP-9999 (item 6): the app-wide focus-ring primitive
     (src/styles/focusRing.css, DL-096) deliberately forces
     "input:focus-visible:focus-visible { border-radius: var(--iy-focus-ring-radius) }"
     (4px) on every raw <input>/<textarea>/<select> in the app — a correct fix
     for OTHER fields whose focusable node is square inside a rounded MUI
     container, but a REGRESSION here: Form's controls already carry their own
     8px radius directly, so DL-096's rule shrinks it to 4px on focus (measured
     via getComputedStyle before/after focus). That global rule is deliberate,
     tested (theme/focusRingToken.test.js) app-wide infrastructure and must not
     be weakened — so this reclaims the radius LOCALLY instead, using the exact
     same "doubled pseudo-class" specificity technique that file documents
     (".hash:focus-visible:focus-visible" is (0,0,3,0), which beats the
     global rule's "input:focus-visible:focus-visible" (0,0,2,1) on class/
     pseudo-class count alone, regardless of injection order). Only
     border-radius is reclaimed; border-color/box-shadow above are unaffected
     by DL-096 (it only ever sets border-radius + outline-offset). */
  &:focus-visible:focus-visible {
    border-radius: 8px;
  }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;
};
const Input = styled.input`${controlCss}`;
const Select = styled.select`${controlCss} cursor: pointer;`;
// ISP-9999 (item 2): "Long answer" — same field chrome, multiline.
const Textarea = styled.textarea`
  ${controlCss}
  min-height: 88px;
  padding: 10px 12px;
  line-height: 1.5;
  resize: vertical;
`;
const CharCount = styled.div`
  font-family: ${fontFamily};
  font-size: 11px;
  color: ${field.muted};
  text-align: right;
  margin-top: 2px;
`;
// ISP-9999 (item 5): wraps the date react-datepicker so the calendar icon can
// sit inside the field, matching the builder preview's CalendarIcon exactly.
const DateFieldWrap = styled.div`
  position: relative;
  .react-datepicker-wrapper { display: block; width: 100%; }
`;
const DateIconWrap = styled.span`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  pointer-events: none;
  display: inline-flex;
  color: ${field.muted};
`;
const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${fontFamily};
  font-size: 14px;
  color: ${(p) => p.$ink || field.ink};
  cursor: pointer;
  min-height: 44px;
  input {
    width: 20px;
    height: 20px;
    accent-color: ${(p) => p.$accent || field.accent};
    flex: none;
    cursor: pointer;
  }
`;
const ErrorText = styled.div`
  font-family: ${fontFamily};
  font-size: 11px;
  color: ${field.errorText};
  margin-top: 4px;
`;
// ISP-9999 (item 1): "helper copy and the footer note" — Helper renders BOTH
// (the success-panel sub-copy and the bottom "Responses land in…" note), so
// its color is also resolved via formFieldInk ($ink), defaulting to the
// existing muted grey exactly like Label/Req above.
const Helper = styled.div`
  font-family: ${fontFamily};
  font-size: 12px;
  color: ${(p) => p.$ink || field.muted};
  line-height: 1.4;
`;
const Banner = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fef3f2;
  border: 1px solid #fda29b;
  font-family: ${fontFamily};
  font-size: 13px;
  color: #b42318;
`;
const RetryBtn = styled.button`
  margin-left: auto;
  flex: none;
  border: none;
  background: none;
  color: #b42318;
  font-family: ${fontFamily};
  font-size: 13px;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
  min-height: 32px;
  &:focus-visible { outline: 2px solid ${field.accent}; outline-offset: 2px; border-radius: 4px; }
`;
const Submit = styled.button`
  /* ISP-9999 (item 7): right-justified, with the standard element-to-button
     vertical gap (spacing.xl, 24px — the same token MediaLibrary's
     Footer/UploadCta use between their preceding content and their own CTA
     button) instead of the Wrap's generic 14px content rhythm. */
  align-self: flex-end;
  margin-top: ${spacing.xl}px;
  min-height: 44px;
  padding: 10px 24px;
  border-radius: 10px;
  border: none;
  color: ${(p) => p.$textColor || "#fff"};
  font-family: ${fontFamily};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: ${motion.transition("filter", motion.duration.fast)};
  &:hover:not(:disabled) { filter: brightness(0.96); }
  &:disabled { opacity: 0.6; cursor: default; }
  &:focus-visible { outline: none; box-shadow: ${focusRing.shadow}; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;
const SuccessPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 28px 16px;
  text-align: center;
  font-family: ${fontFamily};
`;
const SuccessTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${field.ink};
`;

// Off-screen honeypot: invisible to humans and to AT, but a plain text input a
// dumb bot will fill. Not display:none (some bots skip hidden inputs).
const honeypotStyle = {
  position: "absolute",
  left: "-9999px",
  width: "1px",
  height: "1px",
  overflow: "hidden",
  opacity: 0,
};

const CheckGlyph = () => (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="11" stroke={brand.primary} strokeWidth="1.6" />
    <path d="M7.5 12.4l3 3 6-6.4" stroke={brand.primary} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const AlertGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flex: "none", marginTop: 1 }}>
    <path d="M12 8v5M12 16.5v.5" stroke="#b42318" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M10.3 3.9 2.7 17.4A2 2 0 0 0 4.4 20.4h15.2a2 2 0 0 0 1.7-3l-7.6-13.5a2 2 0 0 0-3.4 0Z" stroke="#b42318" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);

const FormLiveView = ({
  title,
  fields = [],
  submitLabel = "Send",
  submitColor = brand.primary,
  blockId,
}) => {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [succeeded, setSucceeded] = useState(false);
  const wrapRef = useRef(null);
  const { wheelId } = useParams();
  const segment = useSegmentContext();

  const mutation = useMutation(submitFormResponse, {
    onSuccess: () => setSucceeded(true),
  });

  // ISP-9999 (item 1): resolved ONCE per render against the segment's
  // background (see formFieldInk.js for the "image wallpapers can't be
  // sampled statically" scope note), reused everywhere a label/required-
  // marker/helper color is needed below.
  const labelInk = formFieldInk(segment?.settings, field.ink);
  const requiredInk = formFieldInk(segment?.settings, field.errorText);
  const helperInk = formFieldInk(segment?.settings, field.muted);

  const setValue = (id, v) => {
    setValues((prev) => ({ ...prev, [id]: v }));
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: undefined }));
  };

  const focusFirstInvalid = (errs) => {
    const firstId = fields.find((f) => errs[f.id])?.id;
    if (firstId && wrapRef.current) {
      const el = wrapRef.current.querySelector(`[name="${CSS.escape(firstId)}"]`);
      if (el && el.focus) el.focus();
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (mutation.isLoading) return; // double-submit guard
    const errs = validateForm(fields, values);
    if (Object.keys(errs).length) {
      setErrors(errs);
      focusFirstInvalid(errs);
      return;
    }
    // Silent honeypot drop: pretend success, send nothing.
    if (isHoneypotFilled(values)) {
      setSucceeded(true);
      return;
    }
    mutation.mutate(
      buildResponsePayload({
        wheelId,
        segmentId: segment?.id,
        blockId,
        title,
        fields,
        values,
      })
    );
  };

  if (succeeded) {
    return (
      <SuccessPanel role="status" aria-live="polite">
        <CheckGlyph />
        <SuccessTitle>Thanks — your response was sent.</SuccessTitle>
        <Helper $ink={helperInk}>The wheel owner has received your answers.</Helper>
      </SuccessPanel>
    );
  }

  const renderControl = (f) => {
    const invalid = !!errors[f.id];
    const errId = `${f.id}-err`;
    const common = {
      id: f.id,
      name: f.id,
      "aria-invalid": invalid,
      "aria-required": !!f.required,
      "aria-describedby": invalid ? errId : undefined,
    };

    if (f.type === "checkbox") {
      return (
        <div>
          <CheckRow $accent={submitColor} $ink={labelInk}>
            <input
              type="checkbox"
              {...common}
              checked={values[f.id] === true}
              onChange={(e) => setValue(f.id, e.target.checked)}
            />
            <span>
              {f.label || "Field"}
              {f.required && <Req aria-hidden="true" $ink={requiredInk}>*</Req>}
            </span>
          </CheckRow>
          {invalid && <ErrorText id={errId}>{errors[f.id]}</ErrorText>}
        </div>
      );
    }

    const labelBlock = (
      <Label htmlFor={f.id} $ink={labelInk}>
        {f.label || "Field"}
        {f.required && <Req aria-hidden="true" $ink={requiredInk}>*</Req>}
      </Label>
    );

    // ISP-9999 (item 3): dropdown fields now carry real, authored options
    // (added/renamed/removed/reordered in FormEditor) instead of a single
    // fixed placeholder. Falls back to one empty option for wheels authored
    // before this fix (a dropdown field with no `options` yet).
    if (f.type === "dropdown") {
      const options = f.options?.length ? f.options : [];
      return (
        <div>
          {labelBlock}
          <Select
            {...common}
            $accent={submitColor}
            value={values[f.id] ?? ""}
            onChange={(e) => setValue(f.id, e.target.value)}
          >
            <option value="">Choose…</option>
            {options
              .filter((o) => o && o.text)
              .map((o) => (
                <option key={o.id} value={o.text}>
                  {o.text}
                </option>
              ))}
          </Select>
          {invalid && <ErrorText id={errId}>{errors[f.id]}</ErrorText>}
        </div>
      );
    }

    // ISP-9999 (item 2): "Long answer" — multi-line, 200-char limit with a
    // live counter.
    if (f.type === "longtext") {
      const text = values[f.id] ?? "";
      return (
        <div>
          {labelBlock}
          <Textarea
            {...common}
            $accent={submitColor}
            maxLength={LONGTEXT_MAX}
            placeholder={f.placeholder || DEFAULT_PLACEHOLDER_BY_TYPE.longtext}
            value={text}
            onChange={(e) => setValue(f.id, e.target.value)}
          />
          <CharCount>{text.length}/{LONGTEXT_MAX}</CharCount>
          {invalid && <ErrorText id={errId}>{errors[f.id]}</ErrorText>}
        </div>
      );
    }

    // ISP-9999 (item 5): the app date picker with fast month/year <select>
    // dropdowns (react-datepicker, already an app dependency — DateRange uses
    // it) instead of the slow native year-scroll, rendered through OUR OWN
    // styled <Input> via `customInput` so it inherits the exact same field
    // chrome (border/radius/focus) as every other Form control, and the SAME
    // calendar icon the builder preview uses (item 5's other half).
    if (f.type === "date") {
      return (
        <div>
          {labelBlock}
          <DateFieldWrap>
            <DatePicker
              id={f.id}
              name={f.id}
              selected={fromISODate(values[f.id])}
              onChange={(date) => setValue(f.id, toISODate(date))}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              scrollableYearDropdown
              yearDropdownItemNumber={100}
              placeholderText={f.placeholder || DEFAULT_PLACEHOLDER_BY_TYPE.date || "Pick a date"}
              customInput={
                <Input
                  $accent={submitColor}
                  aria-invalid={invalid}
                  aria-required={!!f.required}
                  aria-describedby={invalid ? errId : undefined}
                  style={{ paddingRight: 36 }}
                />
              }
            />
            <DateIconWrap aria-hidden="true">
              <CalendarIcon size={16} color={field.muted} />
            </DateIconWrap>
          </DateFieldWrap>
          {invalid && <ErrorText id={errId}>{errors[f.id]}</ErrorText>}
        </div>
      );
    }

    // ISP-9999 (item 8): a country-code dropdown (flag + dial code), defaulted
    // from the browser locale, storing E.164 — the SAME shared phone widget
    // Login/Signup/Settings already use, not a bespoke input.
    if (f.type === "phone") {
      return (
        <div>
          {labelBlock}
          <CustomPhoneInput
            {...common}
            value={values[f.id] ?? ""}
            onChange={(v) => setValue(f.id, v?.phoneNumber ?? "")}
            defaultCountry={deriveCountryCode()}
            placeholder={f.placeholder || DEFAULT_PLACEHOLDER_BY_TYPE.phone}
          />
          {invalid && <ErrorText id={errId}>{errors[f.id]}</ErrorText>}
        </div>
      );
    }

    const inputType = f.type === "email" ? "email" : "text";
    // ISP-9999 (item 9): placeholder is its OWN stored property, independent
    // of the label — a type-level default when unset, NEVER derived from
    // whatever the label currently says.
    const placeholder = f.placeholder || DEFAULT_PLACEHOLDER_BY_TYPE[f.type] || DEFAULT_PLACEHOLDER_BY_TYPE.text;

    return (
      <div>
        {labelBlock}
        <Input
          type={inputType}
          {...common}
          $accent={submitColor}
          placeholder={placeholder}
          value={values[f.id] ?? ""}
          onChange={(e) => setValue(f.id, e.target.value)}
        />
        {invalid && <ErrorText id={errId}>{errors[f.id]}</ErrorText>}
      </div>
    );
  };

  return (
    <Wrap ref={wrapRef}>
      <FormEl onSubmit={onSubmit} noValidate>
        {title ? <Title>{title}</Title> : null}
        <Fields>{fields.map((f) => (f && f.id != null ? <React.Fragment key={f.id}>{renderControl(f)}</React.Fragment> : null))}</Fields>

        {/* Honeypot — off-screen, never shown to a human. */}
        <input
          type="text"
          name={HONEYPOT_FIELD}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={honeypotStyle}
          value={values[HONEYPOT_FIELD] ?? ""}
          onChange={(e) => setValue(HONEYPOT_FIELD, e.target.value)}
        />

        {mutation.isError && (
          <Banner role="alert">
            <AlertGlyph />
            <span>Something went wrong. Please try again.</span>
            <RetryBtn type="submit">Retry</RetryBtn>
          </Banner>
        )}

        <Submit
          type="submit"
          style={{ background: submitColor }}
          $textColor={autoTextColor(submitColor, { fallback: "#fff" })}
          disabled={mutation.isLoading}
        >
          {mutation.isLoading ? "Sending…" : submitLabel || "Send"}
        </Submit>

        <Helper $ink={helperInk}>
          Responses land in the wheel owner's Forms inbox — visitors never see
          each other's answers.
        </Helper>
      </FormEl>
    </Wrap>
  );
};

export default FormLiveView;
